import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';

import { UnifiedActivitiesService } from './unified-activities.service';
import { UnifiedActivity, ActivityType } from './unified-activity.model';
import { ActivityDayDialogComponent, ActivityDayDialogResult } from './activity-day-dialog.component';

interface CalendarCell {
  date: Date;
  inCurrentMonth: boolean;
  isToday: boolean;
  activities: UnifiedActivity[];
}

@Component({
  selector: 'app-activity-calendar',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule, MatDialogModule, DatePipe],
  templateUrl: './activity-calendar.component.html',
  styleUrls: ['./activity-calendar.component.scss']
})
export class ActivityCalendarComponent implements OnInit, OnDestroy {
  private activitiesService = inject(UnifiedActivitiesService);
  private dialog = inject(MatDialog);
  // Optional: only present when this component is opened via dialog.open(...),
  // which is how the dashboard widget's "View all" button uses it.
  dialogRef = inject(MatDialogRef<ActivityCalendarComponent>, { optional: true });
  private sub?: Subscription;

  loading = true;
  viewDate = new Date();
  weeks: CalendarCell[][] = [];

  private allActivities: UnifiedActivity[] = [];

  readonly weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  load(): void {
    this.loading = true;
    this.sub = this.activitiesService.getUpcomingActivities().subscribe({
      next: (activities) => {
        this.allActivities = activities;
        this.buildCalendar();
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  get monthLabel(): string {
    return this.viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }

  prevMonth(): void {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    this.buildCalendar();
  }

  nextMonth(): void {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    this.buildCalendar();
  }

  goToday(): void {
    this.viewDate = new Date();
    this.buildCalendar();
  }

  dotColorFor(type: ActivityType): string {
    switch (type) {
      case 'telemedicine': return '#1D9E75';
      case 'event': return '#D85A30';
      default: return '#378ADD';
    }
  }

  openDay(cell: CalendarCell): void {
    const dialogRef = this.dialog.open(ActivityDayDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      data: { date: cell.date, activities: cell.activities }
    });

    dialogRef.afterClosed().subscribe((result: ActivityDayDialogResult | undefined) => {
      if (result?.action === 'view-details') {
        // TODO: wire to your existing AppointmentDetailsComponent / EventDetailsDialogComponent
      }
    });
  }

  private buildCalendar(): void {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();

    const firstOfMonth = new Date(year, month, 1);
    const startOffset = firstOfMonth.getDay(); // 0 = Sunday
    const gridStart = new Date(year, month, 1 - startOffset);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const activitiesByDay = this.groupActivitiesByDay(this.allActivities);

    const weeks: CalendarCell[][] = [];
    let cursor = new Date(gridStart);

    // Always render 6 weeks (42 days) so the grid height is stable across months.
    for (let week = 0; week < 6; week++) {
      const row: CalendarCell[] = [];
      for (let day = 0; day < 7; day++) {
        const cellDate = new Date(cursor);
        const key = cellDate.toDateString();
        row.push({
          date: cellDate,
          inCurrentMonth: cellDate.getMonth() === month,
          isToday: cellDate.toDateString() === today.toDateString(),
          activities: activitiesByDay.get(key) ?? []
        });
        cursor.setDate(cursor.getDate() + 1);
      }
      weeks.push(row);
    }

    this.weeks = weeks;
  }

  private groupActivitiesByDay(activities: UnifiedActivity[]): Map<string, UnifiedActivity[]> {
    const map = new Map<string, UnifiedActivity[]>();
    for (const activity of activities) {
      if (!activity.start) continue;
      const d = new Date(activity.start);
      if (isNaN(d.getTime())) continue;
      const key = d.toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(activity);
    }
    return map;
  }
}
